import { Component, OnInit, ViewChildren, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HistoricalService, HistoricalRelations, HistoricalPartnerRef } from '../../services/historical.service';
import { Recipient } from '../../interfaces/recipient';
import { Donor } from '../../interfaces/donor';
import { PagetoolbarComponent } from '../pagetoolbar/pagetoolbar.component';
import { ParticipantBoxComponent } from '../participant/participant-box/participant-box.component';

// A recipient or donor from the historical list, tagged with its participant type.
export type HistoricalParticipant = (Recipient | Donor) & { type: 'Recipient' | 'Donor' };

@Component({
  selector: 'app-historical-participants',
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatButtonToggleModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    PagetoolbarComponent,
    ParticipantBoxComponent
  ],
  templateUrl: './historical-participants.component.html',
  styleUrl: './historical-participants.component.scss'
})
export class HistoricalParticipantsComponent implements OnInit {
  @ViewChildren(MatExpansionPanel) panels!: QueryList<MatExpansionPanel>;

  // Single merged list of recipients + donors
  participants: HistoricalParticipant[] = [];
  filteredParticipants: HistoricalParticipant[] = [];

  searchControl = new FormControl('');
  transplantedFilter: 'all' | 'transplanted' | 'not-transplanted' = 'all';
  participantType: 'all' | 'recipients' | 'donors' = 'all';

  // Lazy-loaded pair/transplant relationships, cached per ENIS number
  relationsByEnis: Record<string, HistoricalRelations> = {};
  relationsLoading: Record<string, boolean> = {};

  // Reasons that indicate a cross-over transplantation (shown with the green badge)
  private readonly transplantedReasons = [
    'Transplanted within cross-over program',
    'Transplanted with postmortal kidney',
    'Transplanted with kidney of other living donor',
    'Transplanted with kidney of an altruist in domino chain',
    'Transplanted regardless of blood group incompatibility',
    'Transplanted regardless of HLA incompatibility',
    'transplanted' // From auto-marking when a chain is transplanted
  ];

  constructor(private historicalService: HistoricalService) {}

  ngOnInit(): void {
    this.loadHistoricalParticipants();

    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.applyFilters();
    });
  }

  loadHistoricalParticipants(): void {
    const transplantedParam = this.transplantedFilter === 'all' ? undefined : this.transplantedFilter === 'transplanted';

    let recipients: HistoricalParticipant[] | null = null;
    let donors: HistoricalParticipant[] | null = null;

    const merge = () => {
      if (recipients === null || donors === null) return;
      this.participants = [...recipients, ...donors].sort((a, b) => this.archivedTime(b) - this.archivedTime(a));
      this.applyFilters();
    };

    this.historicalService.getHistoricalRecipients(transplantedParam).subscribe({
      next: (data) => {
        recipients = data.map((r) => ({ ...r, type: 'Recipient' as const }));
        merge();
      },
      error: (error) => {
        console.error('Error loading historical recipients:', error);
        recipients = [];
        merge();
      }
    });

    this.historicalService.getHistoricalDonors(transplantedParam).subscribe({
      next: (data) => {
        donors = data.map((d) => ({ ...d, type: 'Donor' as const }));
        merge();
      },
      error: (error) => {
        console.error('Error loading historical donors:', error);
        donors = [];
        merge();
      }
    });
  }

  private archivedTime(participant: HistoricalParticipant): number {
    return participant.historicalDate ? new Date(participant.historicalDate).getTime() : 0;
  }

  applyFilters(): void {
    const searchTerm = this.searchControl.value?.toLowerCase() || '';

    this.filteredParticipants = this.participants.filter((participant) => {
      // Type filter
      if (this.participantType === 'recipients' && participant.type !== 'Recipient') return false;
      if (this.participantType === 'donors' && participant.type !== 'Donor') return false;

      // Search filter
      if (!searchTerm) return true;
      return (
        participant.enisNumber.toLowerCase().includes(searchTerm) ||
        participant.firstName?.toLowerCase().includes(searchTerm) ||
        participant.lastName?.toLowerCase().includes(searchTerm) ||
        participant.center?.name?.toLowerCase().includes(searchTerm) ||
        participant.historicalReason?.toLowerCase().includes(searchTerm)
      );
    });
  }

  onTransplantedFilterChange(filter: 'all' | 'transplanted' | 'not-transplanted'): void {
    this.transplantedFilter = filter;
    this.loadHistoricalParticipants();
  }

  onParticipantTypeChange(type: 'all' | 'recipients' | 'donors'): void {
    this.participantType = type;
    this.applyFilters();
  }

  // Type label shown as the first column (matches the participants screen: Recipient / Donor / Altruist)
  typeLabel(participant: HistoricalParticipant): string {
    if (participant.type === 'Donor') {
      return (participant as Donor).altruist ? 'Altruist' : 'Donor';
    }
    return 'Recipient';
  }

  // Green badge when the archive reason indicates a cross-over transplantation
  isTransplantedReason(reason?: string): boolean {
    return !!reason && this.transplantedReasons.includes(reason);
  }

  // Load pair/transplant relationships once when a participant is expanded
  onPanelOpened(participant: HistoricalParticipant): void {
    const enis = participant.enisNumber;
    if (this.relationsByEnis[enis] || this.relationsLoading[enis]) return;

    this.relationsLoading[enis] = true;
    const type = participant.type === 'Recipient' ? 'recipient' : 'donor';
    this.historicalService.getRelations(type, enis).subscribe({
      next: (relations) => {
        this.relationsByEnis[enis] = relations;
        this.relationsLoading[enis] = false;
      },
      error: (error) => {
        console.error('Error loading historical relations:', error);
        this.relationsByEnis[enis] = { pairedWith: [], transplantedWith: null };
        this.relationsLoading[enis] = false;
      }
    });
  }

  // Whether a related partner is present in the current historical list (so we can link to its card)
  isInHistoricalList(enisNumber: string): boolean {
    return this.participants.some((p) => p.enisNumber === enisNumber);
  }

  // Jump to a partner's card within this historical overview: clear filters that could hide it,
  // then expand and scroll to it.
  goToPartnerCard(partner: HistoricalPartnerRef): void {
    if (!this.isInHistoricalList(partner.enisNumber)) return;

    // Make sure the target is not filtered out
    this.participantType = 'all';
    if (this.searchControl.value) {
      this.searchControl.setValue('', { emitEvent: false });
    }
    this.applyFilters();

    const index = this.filteredParticipants.findIndex((p) => p.enisNumber === partner.enisNumber);
    if (index === -1) return;

    setTimeout(() => {
      // Collapse every other card so the target is not pushed down, then open only the target
      const panels = this.panels?.toArray() ?? [];
      panels.forEach((panel, i) => (i === index ? panel.open() : panel.close()));

      // Scroll after animations have settled (Material expansion animation is 225ms; 300ms gives a
      // safe margin so panels above the target finish collapsing before we measure its position).
      setTimeout(() => {
        document.getElementById('hist-' + partner.enisNumber)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }, 100);
  }

  asRecipient(participant: HistoricalParticipant): Recipient {
    return participant;
  }

  asDonor(participant: HistoricalParticipant): Donor {
    return participant;
  }
}
